'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { MinimalWave } from '@/contexts/wave/hooks/useEnhancedWavesList';
import { usePrefetchWaveData } from '@/hooks/usePrefetchWaveData';
import useDeviceInfo from '@/hooks/useDeviceInfo';
import { ApiWaveType } from '@/generated/models/ApiWaveType';
import { CollapsedWave } from './subcomponents/CollapsedWave';
import { ExpandedWave } from './subcomponents/ExpandedWave';
import { useWaveNavigation } from './hooks/useWaveNavigation';
import { useWaveNameTruncation } from './hooks/useWaveNameTruncation';
import { formatWaveName } from './utils/formatWaveName';
interface WebBrainLeftSidebarWaveProps {
  readonly wave: MinimalWave;
  readonly onHover: (waveId: string) => void;
  readonly showPin?: boolean;
  readonly basePath?: string;
  readonly collapsed?: boolean;
}
const TOOLTIP_PLACEMENT = 'right' as const;
const WebBrainLeftSidebarWave = t0 => {
  const $ = _c(34);
  const {
    wave,
    onHover,
    showPin: t1,
    basePath: t2,
    collapsed: t3
  } = t0;
  const showPin = t1 === undefined ? true : t1;
  const basePath = t2 === undefined ? "/waves" : t2;
  const collapsed = t3 === undefined ? false : t3;
  const searchParams = useSearchParams();
  const prefetchWaveData = usePrefetchWaveData();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let t4;
  if ($[0] !== basePath || $[1] !== onHover || $[2] !== prefetchWaveData || $[3] !== searchParams || $[4] !== wave.id) {
    t4 = {
      basePath,
      onHover,
      prefetchWaveData,
      searchParams,
      waveId: wave.id
    };
    $[0] = basePath;
    $[1] = onHover;
    $[2] = prefetchWaveData;
    $[3] = searchParams;
    $[4] = wave.id;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    href,
    isActive,
    onMouseEnter
  } = useWaveNavigation(t4);
  let t5;
  if ($[6] !== wave.name || $[7] !== wave.type) {
    t5 = formatWaveName({
      name: wave.name,
      type: wave.type
    });
    $[6] = wave.name;
    $[7] = wave.type;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const formattedWaveName = t5;
  let t6;
  if ($[9] !== collapsed || $[10] !== formattedWaveName) {
    t6 = {
      collapsed,
      contentKey: formattedWaveName
    };
    $[9] = collapsed;
    $[10] = formattedWaveName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    nameRef,
    isNameTruncated
  } = useWaveNameTruncation(t6);
  const isDropWave = wave.type !== ApiWaveType.Chat;
  const haveNewDrops = wave.newDropsCount.count > 0;
  const tooltipId = `wave-collapsed-${wave.id}`;
  const expandedTooltipId = `wave-expanded-${wave.id}`;
  const showCollapsedTooltip = collapsed && !hasTouchScreen;
  const showExpandedTooltip = !collapsed && !hasTouchScreen && isNameTruncated;
  if (collapsed) {
    let t7;
    if ($[12] !== formattedWaveName || $[13] !== haveNewDrops || $[14] !== href || $[15] !== isActive || $[16] !== isDropWave || $[17] !== onMouseEnter || $[18] !== showCollapsedTooltip || $[19] !== tooltipId || $[20] !== wave) {
      t7 = <CollapsedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} onMouseEnter={onMouseEnter} showTooltip={showCollapsedTooltip} tooltipId={tooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} />;
      $[12] = formattedWaveName;
      $[13] = haveNewDrops;
      $[14] = href;
      $[15] = isActive;
      $[16] = isDropWave;
      $[17] = onMouseEnter;
      $[18] = showCollapsedTooltip;
      $[19] = tooltipId;
      $[20] = wave;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  let t7;
  if ($[22] !== expandedTooltipId || $[23] !== formattedWaveName || $[24] !== haveNewDrops || $[25] !== href || $[26] !== isActive || $[27] !== isDropWave || $[28] !== nameRef || $[29] !== onMouseEnter || $[30] !== showExpandedTooltip || $[31] !== showPin || $[32] !== wave) {
    t7 = <ExpandedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} latestDropTimestamp={wave.newDropsCount.latestDropTimestamp} nameRef={nameRef} onMouseEnter={onMouseEnter} showExpandedTooltip={showExpandedTooltip} showPin={showPin} tooltipContent={formattedWaveName} tooltipId={expandedTooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} waveId={wave.id} isPinned={wave.isPinned} />;
    $[22] = expandedTooltipId;
    $[23] = formattedWaveName;
    $[24] = haveNewDrops;
    $[25] = href;
    $[26] = isActive;
    $[27] = isDropWave;
    $[28] = nameRef;
    $[29] = onMouseEnter;
    $[30] = showExpandedTooltip;
    $[31] = showPin;
    $[32] = wave;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
};
export default WebBrainLeftSidebarWave;